import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";

export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export function buildErrorDetailsMarkup(error: Error | undefined): string {
  if (!error) return "";
  const errorDetails = escapeHtml(error.stack || error.toString());
  return `<details style="margin-top: 1rem; text-align: left; font-size: 0.75rem; color: #6b7280;"><summary style="cursor: pointer;">Technical Details</summary><pre style="margin-top: 0.5rem; padding: 0.5rem; background: #f3f4f6; border-radius: 0.25rem; overflow: auto;">${errorDetails}</pre></details>`;
}

/**
 * Build Root Error Markup.
 */
export function buildRootErrorMarkup(message: string, error?: Error): string {
  const escapedMessage = escapeHtml(message);
  const errorDetails = buildErrorDetailsMarkup(error);

  return `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: white; font-family: system-ui;">
        <div style="text-align: center; padding: 2rem; max-width: 500px;">
          <h1 style="color: #1f2937; margin-bottom: 1rem; font-size: 1.5rem;">TellerWand</h1>
          <div style="color: #dc2626; margin-bottom: 1rem; font-size: 0.875rem;">${escapedMessage}</div>
          <p style="color: #6b7280; margin-bottom: 1.5rem; font-size: 0.875rem;">Please try refreshing the page. If the problem persists, contact support.</p>
          <button onclick="window.location.reload()" style="padding: 0.75rem 1.5rem; background: #2563eb; color: white; border: none; border-radius: 0.375rem; cursor: pointer; font-size: 0.875rem;">Reload Page</button>
          ${errorDetails}
        </div>
      </div>
    `;
}

export function buildMissingRootMarkup(): string {
  return `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: white; font-family: system-ui;">
        <div style="text-align: center; padding: 2rem;">
          <h1 style="color: #dc2626;">Error: Root element not found</h1>
          <p style="color: #6b7280;">Please contact support.</p>
        </div>
      </div>
    `;
}

export function showErrorUI(rootElement: HTMLElement | null, message: string, error?: Error): void {
  const errorMessage = error ? `${message}: ${error.message}` : message;

  if (rootElement) {
    rootElement.innerHTML = buildRootErrorMarkup(errorMessage, error);
    return;
  }

  document.body.innerHTML = buildMissingRootMarkup();
}

export function verifyReactDependencies(): void {
  if (!ReactDOM?.createRoot) {
    throw new Error(
      "ReactDOM.createRoot is not available. This may indicate a build or bundling issue."
    );
  }

  if (!StrictMode) {
    throw new Error("React.StrictMode is not available. This may indicate a React import issue.");
  }
}

/**
 * Mount React App.
 */
export function mountReactApp(rootElement: HTMLElement): void {
  verifyReactDependencies();

  if (process.env.NODE_ENV === "development") {
    console.warn("🔍 Starting React initialization...");
    console.warn("✅ React and ReactDOM verified");
    console.warn("🔍 Creating React root...");
  }

  const root = ReactDOM.createRoot(rootElement);

  if (process.env.NODE_ENV === "development") {
    console.warn("🔍 Rendering App component...");
  }

  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );

  if (process.env.NODE_ENV === "development") {
    console.warn("✅ React app mounted successfully");
  }
}

/**
 * Format Mount Error.
 */
export function formatMountError(error: unknown): {
  message: string;
  stack?: string;
  name?: string;
} {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      name: error.name,
    };
  }
  if (typeof error === "string") {
    return { message: error };
  }
  return { message: "Unknown error" };
}
